import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FormulaIcon from 'ui-library/icons/FormulaIcon';

// A map of function category name to sorting index of that category's group in the menu.
const FUNCTION_GROUP_ORDER: {[functionCategoryName: string]: number} = objectFromEntries(
  [
    FunctionCategory.MATH,
    FunctionCategory.STATS,
    FunctionCategory.TEXT,
    FunctionCategory.LOGIC,
    FunctionCategory.LOOKUP,
  ].map((category, i) => [category, i])
);

interface BaseFunction {
  name: string;
}

function FunctionOption({
  fn,
  onFunctionClick,
}: {
  fn: BaseFunction;
  onFunctionClick: (fn: BaseFunction) => void;
}): JSX.Element {
  return (
    <li>
      <a
        role="menuitem"
        tabIndex={-1}
        onClick={() => {
          onFunctionClick(fn);
        }}
      >
        {fn.name}
      </a>
    </li>
  );
}

export interface FunctionDropdownProps {
  primaryFunctions: Array<BaseFunction & {category: FunctionCategory | null}>;
  additionalFunctions?: Array<BaseFunction>;

  disabled?: boolean;
  className?: string;
  isOpen: boolean;
  setDropdownOpenState: (isOpen: boolean) => void;
  onFunctionClick: (fn: BaseFunction) => void;
}

export default function FunctionDropdown(props: FunctionDropdownProps): JSX.Element {
  // An array of function categories, each of which is an array of functions in that category.
  const functionGroups: Array<Array<BaseFunction>> = _.chain(props.primaryFunctions)
    .filter((func) => func.category != null)
    .groupBy('category')
    .values()
    .sortBy((func) => FUNCTION_GROUP_ORDER[func[0].category!])
    .map((groupedFunctions) => _.sortBy(groupedFunctions, 'name'))
    .value();
  let functionOptions = functionGroups.map((functions, i) => {
    const items = functions.map((fn) => (
      <FunctionOption key={fn.name} fn={fn} onFunctionClick={props.onFunctionClick} />
    ));
    if (i !== functionGroups.length - 1) {
      items.push(<li key={i} className="divider" />);
    }
    return items;
  });
  if ((props.additionalFunctions?.length ?? 0) > 0) {
    functionOptions = functionOptions.concat([
      <li key="additionalFunctions" className="divider" />,
      ...props.additionalFunctions!.map((fn) => (
        <FunctionOption key={fn.name} fn={fn} onFunctionClick={props.onFunctionClick} />
      )),
    ]);
  }

  return (
    <AbsoluteDropdownContainer
      menu={
        <ul className="dropdown-menu scrollable" role="menu" aria-label="Formula functions">
          {functionOptions}
        </ul>
      }
      onRequestClose={() => {
        props.setDropdownOpenState(false);
      }}
      open={props.isOpen}
      shouldCloseOnInsideClick={true}
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={props.className}
        icon={<FormulaIcon />}
        iconAltText="Formula functions"
        isDropdown={true}
        onClick={() => {
          props.setDropdownOpenState(true);
        }}
        size={ButtonSize.MINI}
        disabled={props.disabled}
      />
    </AbsoluteDropdownContainer>
  );
}
